import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "wouter";
import { ChevronLeft, ChevronRight, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { useCapture } from "@/contexts/CaptureContext";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { weekStartUTC, weekKey } from "@shared/workweek";
import { initialWeekOffsetFromUrl } from "@/lib/weekParam";
import { TabScaffold, mergeTabQueries } from "@/components/tabs/TabScaffold";
import { AppTopBar } from "@/components/home/AppTopBar";
import { ReceiptActionsCard, type ReceiptActionRow } from "@/components/home/ReceiptActionsCard";
import { CaptureFirstCard } from "@/components/home/CaptureFirstCard";
import { WorkWeekStrip } from "@/components/home/WorkWeekStrip";
import { showCaptureFirst } from "@/lib/homeEmptyState";
import { WeeklyProgressCard } from "@/components/home/WeeklyProgressCard";
import { MyPrioritiesCard } from "@/components/home/MyPrioritiesCard";
import {
  markPersonalPriorityActivity,
  personalPriorityActivityAt,
} from "@/lib/personalPriorityActivity";
import { ThisWeekFocusCard } from "@/components/home/ThisWeekFocusCard";
import { ConnectDirectionDialog } from "@/components/home/ConnectDirectionDialog";
import { PersonalPriorityEditorDialog } from "@/components/home/PersonalPriorityEditorDialog";
import { PersonalPriorityDeleteDialog } from "@/components/home/PersonalPriorityDeleteDialog";
import { PersonalPriorityDropDialog } from "@/components/home/PersonalPriorityDropDialog";
// Reuse the SAME neutral unlink confirmation the Team page uses — one shared
// dialog, one copy, consistent behavior (it's a pure presentational component
// with no team-specific logic).
import { UnlinkPersonalPriorityDialog } from "@/components/team/UnlinkPersonalPriorityDialog";
import type { DayStatus } from "@shared/trustLabels";

const NOW = new Date();
const NO_ACTIVITY_MSG = (isAr: boolean) =>
  isAr
    ? "لم يتم التقاط أي نشاط بعد. التقط تحديثًا أولًا، ثم أنشئ مسودة ملخّصك."
    : "No activity captured yet. Capture an update first, then draft your receipt.";
const NO_WEEKLY_ACTIVITY_MSG = (isAr: boolean) =>
  isAr
    ? "لا يوجد نشاط أسبوعي بعد. التقط تحديثًا أولًا، ثم أنشئ مسودة ملخّصك الأسبوعي."
    : "No weekly activity yet. Capture an update first, then draft your weekly receipt.";
const DRAFT_ERROR_MSG = (isAr: boolean) =>
  isAr ? "تعذّر إنشاء المسودة. يرجى المحاولة مرة أخرى." : "Couldn’t create the draft. Please try again.";

/**
 * Home tab — overview ("what should I do now?"). Thin client: consumes the
 * server-safe endpoints and renders. The work-week strip, per-day status and
 * the workday denominator come from `receipts.weekStatus` (server, local-tz);
 * the client performs NO date bucketing and NO sensitive computation.
 */
export default function Home() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;
  // Receipt SHAREABILITY is a property of the workspace the receipt belongs to.
  // In a personal workspace there is no team audience → drop team-sharing
  // language ("Visible to team", "approve & share"). Copy only; mechanics
  // unchanged. (Distinct from the admin-gating rule — this correctly keys on
  // the active workspace's isPersonal flag.)
  const wsIsPersonal = ws.active?.team.isPersonal ?? false;
  const { openCapture } = useCapture();
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [weekOffset, setWeekOffset] = useState(initialWeekOffsetFromUrl);
  // Step A — personal-priority edit/delete (Stage 1, owner-only, private).
  const [personalEditor, setPersonalEditor] = useState<
    { id: number; title: string; description: string; priority: number } | null
  >(null);
  const [personalDeleteTarget, setPersonalDeleteTarget] = useState<
    { id: number; title: string } | null
  >(null);
  // Drop is now CONFIRMED (mobile-polish): dropping is reversible, so it gets a
  // neutral confirm dialog (never destructive-red). Restore stays immediate.
  const [personalDropTarget, setPersonalDropTarget] = useState<
    { id: number; title: string } | null
  >(null);
  // Unlink is now CONFIRMED here too (consistency with the Team page): hold
  // ONLY the (personalPriorityId, teamPriorityId) pair — no other-user/count
  // data — so the confirm stays owner-only and unlinks only THAT one link.
  const [unlinkTarget, setUnlinkTarget] = useState<
    { personalPriorityId: number; teamPriorityId: number } | null
  >(null);
  // Terminology/form cleanup — Home create uses the UNIFIED full form (same
  // dialog as edit + create-for-this), replacing the title-only inline add
  // that could not select an Importance (the no-Low bug).
  const [personalCreatorOpen, setPersonalCreatorOpen] = useState(false);
  // Link-activity ordering — bumped on an in-session link/unlink so the
  // activityById snapshot recomputes even when React Query structural-sharing
  // hands back the same personalPriorities.forWeek reference (link/unlink
  // doesn't change any personal-priority row). Panel-originated activity is
  // picked up on Home's next mount, which reads the store directly.
  const [activityTick, setActivityTick] = useState(0);

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;
  const weekStart = useMemo(() => {
    const b = weekStartUTC(NOW, weekStartDay);
    b.setUTCDate(b.getUTCDate() + weekOffset * 7);
    return b;
  }, [weekStartDay, weekOffset]);
  const weekStartKey = useMemo(() => weekKey(weekStart, weekStartDay), [weekStart, weekStartDay]);
  const todayKey = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD

  const enabled = isAuthenticated && teamId != null;
  const forWeek = trpc.receipts.forWeek.useQuery({ weekStartKey, workspaceTeamId: teamId }, { enabled });
  const weekStatus = trpc.receipts.weekStatus.useQuery({ weekStartKey, workspaceTeamId: teamId }, { enabled });
  // Scope the per-workspace momentum metrics (daily streak, blockers-resolved,
  // signals-this-week) to the active workspace, like the sibling reads above.
  // The weekly streak inside the result stays user-global server-side.
  const momentum = trpc.momentum.summary.useQuery(
    { todayKey, weekStartKey, workspaceTeamId: teamId },
    { enabled },
  );
  // For a manager this returns the whole team's priorities (server-gated); for a
  // member, only their own. We split Mine/Team client-side from this one query.
  const priorities = trpc.priorities.forWeek.useQuery({ teamId: teamId!, weekStartKey }, { enabled });
  // Stage 1 — personal priorities are STRUCTURALLY PRIVATE: the server
  // returns only the caller's own rows (no client-side userId filter needed).
  // Stage 3.5 — the card now lives on EVERY Home workspace (team included):
  // My priorities is the canonical personal-work surface, so users can always
  // find and manage their own work from where they actually are.
  const personalPrioritiesQ = trpc.personalPriorities.forWeek.useQuery(
    { weekStartKey },
    { enabled },
  );
  // Stage 3.5 — the caller's OWN connected-to tags (owner-only inverse read:
  // one raw row per OWN link; no grouping/counting by team priority — that
  // rollup shape is the forbidden Stage 3D summary).
  const myLinksQ = trpc.personalPriorityLinks.mineForWeek.useQuery(
    { weekStartKey },
    { enabled },
  );
  // Stage 3.5 — connect-from-My-priorities picker source: the EXISTING
  // member-gated teamPriorities.list (sanitized projection), fetched lazily
  // only while the picker is open and only in a team workspace.
  // Stage 1 (weekly operating model) — the list is now PERSISTENT (no period):
  // the picker offers every ACTIVE team direction, not just ones authored in
  // the viewed week. The dialog itself filters to teamStatus === "active".
  const [connectTarget, setConnectTarget] = useState<{ id: number; title: string } | null>(null);
  // Member-gated shared read. It feeds TWO consumers now: the connect picker
  // (as before) and the "This week" orientation, which needs a title for each
  // focused id. It is no longer picker-gated for that reason — the same safe
  // projection, read once per team workspace instead of once per picker open.
  const teamDirectionsQ = trpc.teamPriorities.list.useQuery(
    { teamId: teamId! },
    { enabled: enabled && !wsIsPersonal },
  );
  // Which of those directions the team put in focus this week. The server
  // projection is {teamPriorityId, weekStartKey, note} behind assertTeamMember:
  // no author, no counts, no participation. Home reads it and never writes it.
  const weeklyFocusQ = trpc.weeklyFocus.list.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled: enabled && !wsIsPersonal },
  );
  // Optional (does not block the page): self follow-through trend.
  const trajectory = trpc.insights.trajectory.useQuery({ weeks: 8, workspaceTeamId: teamId }, { enabled });
  const followThroughPct =
    trajectory.data?.avgCompletionRate != null ? trajectory.data.avgCompletionRate * 100 : null;

  // Titles for the focused directions, ordered by the directions list so the
  // order is deterministic. Titles only — the focus row's note is deliberately
  // dropped here; Home orients, the Team page explains.
  //
  // ACTIVE ONLY, and this filter is load-bearing. `teamPriorities.list` returns
  // every row for the team regardless of status, and archiving a direction does
  // NOT clear its weekly-focus row — so an archived or done direction still
  // intersects the focused ids. Without this filter Home presented retired work
  // as the current week's focus, which no other consumer of this read does:
  // ConnectDirectionDialog, TeamDirectionCard, WeeklyReportWizard and the Team
  // page all filter to `teamStatus === "active"`. Home matches them rather than
  // inventing its own definition of current.
  /**
   * The focused ACTIVE directions for the viewed week, in one ordered list.
   * `focusTitles` and the owner's connected work are both derived from THIS, so
   * they are index-aligned by construction and cannot drift apart.
   */
  const focusedDirections = useMemo(() => {
    const focused = new Set((weeklyFocusQ.data ?? []).map(f => f.teamPriorityId));
    return (teamDirectionsQ.data ?? []).filter(
      d => d.teamStatus === "active" && focused.has(d.id),
    );
  }, [weeklyFocusQ.data, teamDirectionsQ.data]);

  const focusTitles = useMemo(
    () => focusedDirections.map(d => d.title),
    [focusedDirections],
  );

  /**
   * OWNER-ONLY join, computed from reads Home ALREADY performs for
   * MyPrioritiesCard: the viewer's own links (`myLinksQ`) and their own
   * personal priorities (`personalPrioritiesQ`), intersected with the focused
   * ACTIVE directions. The result is INDEX-ALIGNED with `focusTitles`: entry i
   * holds the viewer's OWN connected priority titles for focusedDirections[i].
   * Identity is the direction's numeric id; the title is display text only.
   *
   * NO new query, NO grouped endpoint, NO other person's data: every row here
   * originates from a read scoped to `ctx.user.id` on the server. It exists so
   * the owner can see their own work under the direction the team put in focus,
   * which is the private half of the weekly chain. It is passed ONLY to
   * ThisWeekFocusCard on Home and never reaches a team or report surface.
   */
  const connectedByFocusIndex = useMemo(() => {
    // Grouped by the direction's NUMERIC id, never by its title.
    //
    // WHY THAT MATTERS, twice over. A title is user-authored text with no
    // uniqueness constraint, so keying a plain object by it (a) merged two
    // same-named directions into one bucket, misattributing the owner's own
    // work between them, and (b) let a direction titled `constructor`,
    // `hasOwnProperty` or `isPrototypeOf` resolve to an inherited
    // Object.prototype member instead of undefined — non-nullish, so `?? []`
    // never fired and `.map` threw, taking the whole app down through the root
    // ErrorBoundary. An id-keyed Map has neither failure mode, and the result
    // is index-aligned to `focusTitles` so the card needs no key at all.
    const ownTitleById = new Map(
      (personalPrioritiesQ.data ?? []).map(p => [p.id, p.title] as const),
    );
    const byDirectionId = new Map<number, string[]>();
    for (const l of myLinksQ.data ?? []) {
      const ownTitle = ownTitleById.get(l.personalPriorityId);
      if (!ownTitle) continue;
      const existing = byDirectionId.get(l.teamPriorityId);
      if (existing) existing.push(ownTitle);
      else byDirectionId.set(l.teamPriorityId, [ownTitle]);
    }
    // Links into a direction that is not focused, not active, or belongs to
    // another team simply have no slot here.
    return focusedDirections.map(d => byDirectionId.get(d.id) ?? []);
  }, [focusedDirections, personalPrioritiesQ.data, myLinksQ.data]);

  const utils = trpc.useUtils();
  const invalidate = () => {
    utils.receipts.forWeek.invalidate();
    utils.yourWeek.forWeek.invalidate();
    utils.receipts.weekStatus.invalidate();
    utils.momentum.summary.invalidate();
    utils.priorities.forWeek.invalidate();
    utils.personalPriorities.forWeek.invalidate();
    // Stage 3.5 — connected-to tags + Stage 3 panels reflect link changes.
    utils.personalPriorityLinks.mineForWeek.invalidate();
    utils.personalPriorityLinks.mineForTeamPriority.invalidate();
  };

  // OAuth integration callbacks redirect to `/?integration=…&status=connected`,
  // which now lands on Home (Stage-A cutover). Show the confirmation toast and
  // clean the URL — same behaviour the legacy Canvas provided.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const integ = sp.get("integration");
    if (integ && sp.get("status") === "connected") {
      toast.success(
        isAr
          ? `تم الاتصال بـ ${integ.replace("_", " ")}`
          : `Connected to ${integ.replace("_", " ")}`,
      );
      sp.delete("integration");
      sp.delete("status");
      const q = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
      utils.integrations.list.invalidate();
      utils.signals.forWeek.invalidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const draftDaily = trpc.receipts.draftDaily.useMutation({ onSuccess: invalidate });
  const draftWeekly = trpc.receipts.draftWeekly.useMutation({ onSuccess: invalidate });
  // Stage 1 — personal-priority mutations route to the private personalPriorities
  // table (owner-only; no teamId/userId). Distinct from the team mutations above.
  const createPersonal = trpc.personalPriorities.create.useMutation({ onSuccess: invalidate });
  const setPersonalStatus = trpc.personalPriorities.setStatus.useMutation({ onSuccess: invalidate });
  // Owner-only edit / delete of the caller's OWN personal priority. No teamId,
  // no manager path — the server gates these with assertPersonalPriorityOwner.
  const updatePersonal = trpc.personalPriorities.update.useMutation({
    onSuccess: () => { invalidate(); setPersonalEditor(null); },
  });
  const deletePersonal = trpc.personalPriorities.delete.useMutation({
    onSuccess: () => { invalidate(); setPersonalDeleteTarget(null); },
  });
  // Stage 3.5 — connect/unlink the caller's OWN priority to/from team
  // direction. Ids only; the owner derives from the session server-side.
  const linkPersonal = trpc.personalPriorityLinks.link.useMutation({
    onSuccess: (_data, variables) => {
      // Link is recent activity on this OWN personal priority — float it up
      // on Home (link/unlink doesn't bump personalPriorities.updatedAt).
      markPersonalPriorityActivity(variables.personalPriorityId);
      setActivityTick(t => t + 1);
      invalidate();
      setConnectTarget(null);
    },
  });
  const unlinkPersonal = trpc.personalPriorityLinks.unlink.useMutation({
    onSuccess: (_data, variables) => {
      // Unlink is also recent activity — and the link row is now gone, so the
      // local marker is the ONLY thing that can float the item up.
      markPersonalPriorityActivity(variables.personalPriorityId);
      setActivityTick(t => t + 1);
      invalidate();
      toast.success(
        isAr
          ? "تم فك الربط من توجيه الفريق. تبقى هذه الأولوية الشخصية خاصة بك."
          : "Unlinked from team direction. This personal priority remains private to you.",
      );
    },
  });
  // personalPriorityId → ALL of the owner's own connected-to tags (multi-link
  // fix: one tag PER LINK — the old "first link wins" collapse hid sibling
  // links). Grouping happens HERE, in the render layer, over the flat
  // per-link mineForWeek rows; SQL stays ungrouped (Stage 3D guard). In a
  // team workspace the current-team link sorts first (stable sort keeps
  // createdAt order among the rest). Labeling/ordering only; nothing hidden.
  const connectedTo = useMemo(() => {
    const m: Record<
      number,
      { teamPriorityId: number; title: string | null; teamId: number | null }[]
    > = {};
    for (const l of myLinksQ.data ?? []) {
      (m[l.personalPriorityId] ??= []).push({
        teamPriorityId: l.teamPriorityId,
        title: l.teamPriorityTitle,
        teamId: l.teamId,
      });
    }
    // Ordering (current-team first) happens in the CARD, which owns
    // currentTeamId and the render; this map is grouping only.
    return m;
  }, [myLinksQ.data]);

  // Link-activity ordering snapshot: personalPriorityId → recent link/unlink
  // timestamp, read from the owner-only client-only marker store. Recomputed
  // on data refetch AND on activityTick (see the tick's declaration). Only the
  // caller's OWN ids are ever present; nothing here is a count or a signal.
  const activityById = useMemo(() => {
    const m: Record<number, number> = {};
    for (const p of personalPrioritiesQ.data ?? []) {
      const at = personalPriorityActivityAt(p.id);
      if (at != null) m[p.id] = at;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalPrioritiesQ.data, activityTick]);

  const state = mergeTabQueries({ forWeek, weekStatus, momentum, priorities });

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setUTCDate(weekStart.getUTCDate() + 6);
    const f = (d: Date) =>
      d.toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    return `${f(weekStart)} — ${f(end)}`;
  }, [weekStart, isAr]);

  // Truthful eyebrow / strip label for whichever week is in view. Pure
  // presentation (derived from the navigation offset) — no sensitive value.
  const weekContextLabel = useMemo(() => {
    if (weekOffset === 0) return isAr ? "هذا الأسبوع" : "This week";
    if (weekOffset === -1) return isAr ? "الأسبوع الماضي" : "Last week";
    if (weekOffset === 1) return isAr ? "الأسبوع القادم" : "Next week";
    const f = weekStart.toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return isAr ? `أسبوع ${f}` : `Week of ${f}`;
  }, [weekOffset, weekStart, isAr]);
  const isCurrentWeek = weekOffset === 0;

  const weekNav = (
    <div className="flex items-center gap-2">
      <button onClick={() => setWeekOffset(o => o - 1)} aria-label={isAr ? "الأسبوع السابق" : "Previous week"} className="rounded-full p-1 hover:bg-muted">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="tabular-nums">{weekLabel}</span>
      <button onClick={() => setWeekOffset(o => o + 1)} aria-label={isAr ? "الأسبوع التالي" : "Next week"} className="rounded-full p-1 hover:bg-muted">
        <ChevronRight className="h-4 w-4" />
      </button>
      {!isCurrentWeek ? (
        <button
          onClick={() => setWeekOffset(0)}
          className="ms-1 inline-flex items-center gap-1 rounded-full bg-coral-soft/60 text-coral px-2.5 py-0.5 text-xs font-medium hover:bg-coral-soft"
        >
          <RotateCcw className="h-3 w-3" /> {isAr ? "هذا الأسبوع" : "This week"}
        </button>
      ) : null}
    </div>
  );

  return (
    <>
    {/* D3 — terminology only. Home is the weekly-receipt hub; the name
        "Your Week" now belongs to the private reflection surface at
        /your-week. No Weekly Receipt behaviour changes. */}
    <TabScaffold
      title={isAr ? "الملخّص الأسبوعي" : "Weekly Receipt"}
      subtitle={weekNav}
      topBar={<AppTopBar weeklyStreak={momentum.data?.weeklyCurrent} />}
      state={state}
    >
      {d => {
        // CAPTURE-FIRST empty state: a fresh, zero-input week must not be
        // pushed into weekly-draft machinery ("0 of N approved / Draft
        // now"). Show a capture-first hero instead until there's real input
        // or approved history. (Decision is a pure, tested helper.)
        // The capture-first HERO is an onboarding screen for a genuinely new
        // user — it must NOT replace the dashboard for an established user/
        // workspace whose CURRENT week merely has no captures yet. A returning
        // user (has a streak, or has priorities) always gets the normal
        // dashboard (Receipt Actions + week strip + priorities); the empty-week
        // states live inside those cards. New user (no streak, no priorities,
        // empty week) still gets the friendly capture-first hero.
        const userHasHistory =
          ((d.momentum as any)?.weeklyCurrent ?? 0) > 0 ||
          d.priorities.length > 0 ||
          (personalPrioritiesQ.data?.length ?? 0) > 0;
        const captureFirst =
          !userHasHistory &&
          showCaptureFirst({
            daySignalCounts: d.weekStatus.days.map(x => x.signalCount),
            dailyReceiptCount: d.forWeek.daily.length,
            approvedWorkdays: d.weekStatus.approvedWorkdays,
            hasWeeklyReceipt: !!d.forWeek.weekly,
          });
        // "My priorities" card — the canonical personal-work surface, rendered
        // on EVERY Home workspace (Stage 3.5: team workspaces included, so a
        // priority created/unlinked from Team Direction is always findable).
        // Rendered in BOTH the capture-first hero and the normal view, so a
        // goal set on a fresh (no-capture-yet) week is never hidden behind the
        // capture prompt. FLAT SELF-ONLY list — this is NOT the (deferred,
        // grouped) Stage 3D Home summary: connected-to tags hang off each OWN
        // personal priority; nothing is grouped or counted by team priority.
        const personalGoalsCard = (
          <MyPrioritiesCard
            priorities={(personalPrioritiesQ.data ?? []) as any}
            view="mine"
            showTeamToggle={false}
            privateNote
            allowDropped
            recentFirst
            activityById={activityById}
            onCreate={() => setPersonalCreatorOpen(true)}
            busyId={setPersonalStatus.isPending ? (setPersonalStatus.variables as any)?.id : null}
            onCycle={(id, next) => setPersonalStatus.mutate({ id, status: next })}
            onEdit={p =>
              setPersonalEditor({
                id: p.id,
                title: p.title,
                description: (p as any).description ?? "",
                priority: (p as any).priority ?? 2,
              })
            }
            onDelete={p => setPersonalDeleteTarget({ id: p.id, title: p.title })}
            onDrop={p => setPersonalDropTarget({ id: p.id, title: p.title })}
            connectedTo={connectedTo}
            currentTeamId={wsIsPersonal ? null : teamId}
            onConnect={
              !wsIsPersonal
                ? p => setConnectTarget({ id: p.id, title: p.title })
                : undefined
            }
            onDisconnect={(p, teamPriorityId) =>
              setUnlinkTarget({ personalPriorityId: p.id, teamPriorityId })
            }
          />
        );
        if (captureFirst) {
          return (
            <>
              <CaptureFirstCard
                isAr={isAr}
                onCapture={() => openCapture(isCurrentWeek ? undefined : weekStartKey)}
                onLearn={() => {
                  if (typeof window !== "undefined")
                    window.dispatchEvent(new CustomEvent("receipt:open-tour"));
                }}
              />
              <WorkWeekStrip
                weekStatus={d.weekStatus}
                label={weekContextLabel}
                todayKey={todayKey}
                onDayClick={dateKey => openCapture(dateKey)}
              />
              {/* Goals you set are visible even before your first capture. */}
              {personalGoalsCard}
            </>
          );
        }

        const todayCell = d.weekStatus.days.find(x => x.dateKey === todayKey);
        const todayStatus = (todayCell?.status ?? "not_started") as DayStatus;

        // Activity gates (server-provided signal counts). Drafting with nothing
        // captured produces an empty placeholder the server re-marks "not
        // started" — so we explain instead of silently no-op'ing.
        const todayHasActivity = (todayCell?.signalCount ?? 0) > 0;
        const weekHasActivity =
          d.weekStatus.days.some(x => x.signalCount > 0) || d.forWeek.daily.length > 0;

        // Today row — server status → compact text + a NON-approving action.
        // Post-approval states keep the approved snapshot LIVE for the team; only
        // the owner's CTA differs (Edit / Review updates / Re-approve).
        const todayApproved = todayStatus === "approved";
        const todayUpdateAvailable = todayStatus === "update_available";
        const todayChangesNotShared = todayStatus === "needs_update";
        const todayRetracted = todayStatus === "retracted";
        // Rest days carry NO daily-receipt action (A6) — without this guard
        // weekend_note fell into the "Review" catch-all and deep-linked to a
        // draft that doesn't exist (the Friday dead-button bug).
        const todayRest = todayStatus === "weekend_note" || todayStatus === "hidden";
        // Retracted = unshared: the team currently sees NOTHING for this day.
        const todayTeamSeesSnapshot = todayApproved || todayUpdateAvailable || todayChangesNotShared;
        const todayStatusText =
          todayStatus === "approved" ? (isAr ? (wsIsPersonal ? "معتمد · خاص بك" : "معتمد · مرئي للفريق") : (wsIsPersonal ? "Approved · Private to you" : "Approved · Visible to team"))
          : todayStatus === "update_available" ? (isAr ? "معتمد · تحديثات جديدة مُلتقطة" : "Approved · New updates captured")
          : todayStatus === "needs_update" ? (isAr ? "تغييرات غير مُشارَكة" : "Changes not shared")
          : todayStatus === "retracted" ? (isAr ? (wsIsPersonal ? "خاص بك" : "مسحوب · غير مرئي للفريق") : (wsIsPersonal ? "Private to you" : "Retracted · not visible to team"))
          : todayRest
            ? (todayCell?.signalCount ?? 0) > 0
              ? isAr
                ? `يوم راحة · ${todayCell!.signalCount} ${todayCell!.signalCount === 1 ? "تحديث مُلتقط" : "تحديثات مُلتقطة"}`
                : `Rest day · ${todayCell!.signalCount} update${todayCell!.signalCount === 1 ? "" : "s"} captured`
              : isAr ? "يوم راحة" : "Rest day"
          : todayStatus === "not_started"
            ? (todayCell?.signalCount ?? 0) > 0
              ? isAr
                ? `${todayCell!.signalCount} ${todayCell!.signalCount === 1 ? "تحديث مُلتقط" : "تحديثات مُلتقطة"}`
                : `${todayCell!.signalCount} update${todayCell!.signalCount === 1 ? "" : "s"} captured`
              : isAr ? "لا يوجد تحديث بعد" : "No update yet"
          : todayStatus === "draft" ? (isAr ? "المسودة جاهزة" : "Draft ready")
          : isAr ? "جاهز للمراجعة" : "Ready to review";
        const reviewToday = () => navigate(`/drafts?review=daily&d=${todayKey}`);
        const todayAction = todayApproved || todayRest
          ? undefined
          : todayRetracted
            ? { label: isAr ? "إعادة اعتماد" : "Re-approve", onClick: reviewToday }
          : todayUpdateAvailable
            ? {
                // Re-draft to fold in the new capture, then open review/edit.
                label: isAr ? "مراجعة التحديثات" : "Review updates",
                onClick: () =>
                  draftDaily.mutate(
                    { date: todayKey, locale: "en", workspaceTeamId: teamId },
                    { onSuccess: reviewToday, onError: () => toast.error(DRAFT_ERROR_MSG(isAr)) },
                  ),
                busy: draftDaily.isPending,
              }
            : todayChangesNotShared
              ? { label: isAr ? "إعادة اعتماد" : "Re-approve", onClick: reviewToday }
              : todayStatus === "not_started"
                ? {
                    label: isAr ? "إنشاء مسودة" : "Draft",
                    // Generate the daily, then open its review/edit flow; explain if
                    // there's nothing to draft; toast on failure (never silent).
                    onClick: () => {
                      if (!todayHasActivity) return void toast.error(NO_ACTIVITY_MSG(isAr));
                      draftDaily.mutate(
                        { date: todayKey, locale: "en", workspaceTeamId: teamId },
                        { onSuccess: reviewToday, onError: () => toast.error(DRAFT_ERROR_MSG(isAr)) },
                      );
                    },
                    busy: draftDaily.isPending,
                  }
                : { label: isAr ? "مراجعة" : "Review", onClick: reviewToday };

        // Weekly row — same post-approval model as a day, driven by the weekly
        // receipt's status + `approvedAt` + the server `weeklyStale` flag.
        const weekly = d.forWeek.weekly as any;
        const weeklyEnum = weekly?.status as "draft" | "edited" | "approved" | "published" | undefined;
        const weeklyEverApproved = weekly?.approvedAt != null;
        const weeklyStale = !!d.forWeek.weeklyStale;
        const weeklyApprovedBase = weeklyEnum === "approved" || weeklyEnum === "published";
        // Owner unshared the weekly — team currently sees nothing for the week.
        const weeklyRetracted = weeklyEverApproved && weekly?.retractedAt != null;
        const weeklyApprovedClean =
          !weeklyRetracted && weeklyEverApproved && weeklyApprovedBase && !weeklyStale;
        // Approved, then content edited after approval (status fell to "edited").
        const weeklyChangesNotShared =
          !weeklyRetracted && weeklyEverApproved && weeklyEnum === "edited";
        // Approved + newer daily activity not yet folded into the weekly.
        const weeklyUpdateAvailable =
          !weeklyRetracted && weeklyEverApproved && weeklyApprovedBase && weeklyStale;
        const weeklyTeamSeesSnapshot = weeklyEverApproved && !weeklyRetracted;
        const weeklyHasDraft = !weeklyEverApproved && (weeklyEnum === "draft" || weeklyEnum === "edited");
        const reviewWeekly = () => navigate("/drafts?review=weekly");
        const weeklyStatusText = weeklyApprovedClean
          ? (isAr ? (wsIsPersonal ? "معتمد · خاص بك" : "معتمد · مرئي للفريق") : (wsIsPersonal ? "Approved · Private to you" : "Approved · Visible to team"))
          : weeklyRetracted
            ? (isAr ? (wsIsPersonal ? "خاص بك" : "مسحوب · غير مرئي للفريق") : (wsIsPersonal ? "Private to you" : "Retracted · not visible to team"))
          : weeklyUpdateAvailable
            ? (isAr ? "معتمد · تحديثات جديدة مُلتقطة" : "Approved · New updates captured")
            : weeklyChangesNotShared
              ? (isAr ? "تغييرات غير مُشارَكة" : "Changes not shared")
              : isAr
                ? `تم اعتماد ${d.weekStatus.approvedWorkdays} من ${d.weekStatus.workdayCount}`
                : `${d.weekStatus.approvedWorkdays} of ${d.weekStatus.workdayCount} approved`;
        const weeklyAction = weeklyApprovedClean
          ? undefined
          : weeklyRetracted
            ? { label: isAr ? "إعادة اعتماد" : "Re-approve", onClick: reviewWeekly }
          : weeklyUpdateAvailable
            ? {
                label: isAr ? "مراجعة التحديثات" : "Review updates",
                onClick: () =>
                  draftWeekly.mutate(
                    { weekStartKey, locale: "en", workspaceTeamId: teamId },
                    { onSuccess: reviewWeekly, onError: () => toast.error(DRAFT_ERROR_MSG(isAr)) },
                  ),
                busy: draftWeekly.isPending,
              }
            : weeklyChangesNotShared
              ? { label: isAr ? "إعادة اعتماد" : "Re-approve", onClick: reviewWeekly }
              : weeklyHasDraft
                ? { label: isAr ? "مراجعة" : "Review", onClick: reviewWeekly }
                : {
                    label: isAr ? "أنشئ المسودة الآن" : "Draft now",
                    onClick: () => {
                      if (!weekHasActivity) {
                        // Open Capture on the week being VIEWED: its first
                        // working day (or today when this is the current week).
                        const captureDay = isCurrentWeek
                          ? undefined
                          : (d.weekStatus.days.find(x => x.isWorkingDay)?.dateKey ?? weekStartKey);
                        return void toast.error(NO_WEEKLY_ACTIVITY_MSG(isAr), {
                          action: { label: isAr ? "التقاط تحديث" : "Capture update", onClick: () => openCapture(captureDay) },
                        });
                      }
                      draftWeekly.mutate(
                        { weekStartKey, locale: "en", workspaceTeamId: teamId },
                        { onSuccess: reviewWeekly, onError: () => toast.error(DRAFT_ERROR_MSG(isAr)) },
                      );
                    },
                    busy: draftWeekly.isPending,
                  };

        // "Today" is about TODAY → only on the current week. Weekly always shows.
        const actionRows: ReceiptActionRow[] = [];
        if (isCurrentWeek)
          actionRows.push({ key: "today", icon: "today", label: isAr ? "ملخّص اليوم" : "Today's receipt", statusText: todayStatusText, approved: todayApproved, teamSeesSnapshot: todayTeamSeesSnapshot, attention: todayUpdateAvailable || todayChangesNotShared || todayRetracted, action: todayAction });
        actionRows.push({ key: "weekly", icon: "weekly", label: isAr ? "الملخّص الأسبوعي" : "Weekly receipt", statusText: weeklyStatusText, approved: weeklyApprovedClean, teamSeesSnapshot: weeklyTeamSeesSnapshot, attention: weeklyUpdateAvailable || weeklyChangesNotShared || weeklyRetracted, action: weeklyAction });
        const allApproved = actionRows.every(r => r.approved);

        return (
          <>
            {/* Act → Orient → Focus → Reflect. */}
            <ReceiptActionsCard rows={actionRows} allApproved={allApproved} isPersonal={wsIsPersonal} />
            <WorkWeekStrip
              weekStatus={d.weekStatus}
              label={weekContextLabel}
              todayKey={todayKey}
              onDayClick={dateKey => openCapture(dateKey)}
            />
            {/* Home answers, in order: what should I do today (above), what am
                I working on (My priorities), and what is this week pointed at
                (This week). The standalone Team Direction shortcut is RETIRED —
                a feature shortcut answered none of those. Team Direction itself
                is unchanged and still lives on the Team page. The old
                assignment-down per-person priorities card and the
                SetTeamPrioritiesDialog entry stay retired (Step C): no
                per-person rows, assignee names, counts, or momentum on Home. */}
            {wsIsPersonal ? (
              personalGoalsCard
            ) : (
              <>
                {personalGoalsCard}
                <ThisWeekFocusCard
                  isAr={isAr}
                  weekLabel={weekContextLabel}
                  titles={focusTitles}
                  connectedByIndex={connectedByFocusIndex}
                  onOpenTeam={() => navigate("/team")}
                />
              </>
            )}
            <WeeklyProgressCard momentum={d.momentum} followThroughPct={followThroughPct} />
            {/* Phase 2A — PRIMARY entry to Your Week (private reflection).
                Carries no status pill and no action verb: reflection has no state
                to advance, unlike the weekly receipt row above it. */}
            <Link href="/your-week" data-testid="home-your-week-card">
              <div className="mt-2.5 flex items-center gap-3 rounded-3xl border border-border/60 bg-card p-4 shadow-sm hover:bg-accent/30">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-sand/50">
                  <Lock className="h-4 w-4 text-coral" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{isAr ? "أسبوعك" : "Your Week"}</span>
                  <span className="block text-xs text-muted-foreground">
                    {isAr ? "مرئية لك وحدك" : "Visible only to you"}
                  </span>
                </span>
              </div>
            </Link>
          </>
        );
      }}
    </TabScaffold>

      <PersonalPriorityEditorDialog
        open={!!personalEditor}
        onOpenChange={o => { if (!o) setPersonalEditor(null); }}
        initial={personalEditor ?? undefined}
        isAr={isAr}
        busy={updatePersonal.isPending}
        onSubmit={vals => {
          if (personalEditor) updatePersonal.mutate({ id: personalEditor.id, ...vals });
        }}
      />
      {/* UNIFIED create form — identical fields/options to edit and to the
          Team-Direction create-for-this flow. The ONLY behavioral difference
          across entry points: Home create makes a personal priority WITHOUT
          any link; create-for-this also auto-links to its team priority. */}
      <PersonalPriorityEditorDialog
        open={personalCreatorOpen}
        onOpenChange={o => { if (!o) setPersonalCreatorOpen(false); }}
        isAr={isAr}
        heading={isAr ? "أنشئ أولوية شخصية" : "Create personal priority"}
        busy={createPersonal.isPending}
        onSubmit={vals =>
          createPersonal.mutate(
            { weekStartKey, ...vals },
            { onSuccess: () => setPersonalCreatorOpen(false) },
          )
        }
      />
      <PersonalPriorityDeleteDialog
        open={!!personalDeleteTarget}
        onOpenChange={o => { if (!o) setPersonalDeleteTarget(null); }}
        isAr={isAr}
        busy={deletePersonal.isPending}
        onConfirm={() => {
          if (personalDeleteTarget) deletePersonal.mutate({ id: personalDeleteTarget.id });
        }}
      />
      {/* Drop confirmation (mobile-polish) — neutral, reversible. Confirms
          before moving a personal priority out of the active list; the
          existing setStatus mutation (with its cache invalidation +
          link-activity ordering) does the work, so drop/restore behavior and
          ordering are unchanged. */}
      <PersonalPriorityDropDialog
        open={!!personalDropTarget}
        onOpenChange={o => { if (!o) setPersonalDropTarget(null); }}
        isAr={isAr}
        busy={setPersonalStatus.isPending}
        onConfirm={() => {
          if (personalDropTarget) {
            setPersonalStatus.mutate(
              { id: personalDropTarget.id, status: "dropped" },
              { onSuccess: () => setPersonalDropTarget(null) },
            );
          }
        }}
      />
      {/* Unlink confirmation (consistency with the Team page) — NEUTRAL,
          reuses the shared dialog. Confirming uses the SAME unlink mutation
          (ids only; its cache invalidation + link-activity ordering are
          unchanged), and removes ONLY this one link — sibling links and the
          personal priority itself remain. Cancel leaves everything intact. */}
      <UnlinkPersonalPriorityDialog
        open={unlinkTarget != null}
        onOpenChange={o => { if (!o) setUnlinkTarget(null); }}
        isAr={isAr}
        busy={unlinkPersonal.isPending}
        onConfirm={() => {
          if (unlinkTarget) {
            unlinkPersonal.mutate(unlinkTarget, {
              onSuccess: () => setUnlinkTarget(null),
            });
          }
        }}
      />
      <ConnectDirectionDialog
        open={!!connectTarget}
        onOpenChange={o => { if (!o) setConnectTarget(null); }}
        isAr={isAr}
        // PER-ITEM exclusion (multi-link fix): hide only the team priorities
        // THIS personal priority is already linked to, using the connected-to
        // rows already on hand. Not global; item B still sees what item A
        // linked. No new endpoint, no new read.
        directions={(teamDirectionsQ.data ?? []).filter(
          d =>
            !connectTarget ||
            !(connectedTo[connectTarget.id] ?? []).some(
              c => c.teamPriorityId === d.id,
            ),
        )}
        loading={teamDirectionsQ.isLoading}
        busy={linkPersonal.isPending}
        personalTitle={connectTarget?.title ?? ""}
        onPick={teamPriorityId => {
          if (connectTarget)
            linkPersonal.mutate({ personalPriorityId: connectTarget.id, teamPriorityId });
        }}
      />
    </>
  );
}
